import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const TZ = 'Europe/Dublin';

// Mobile breakpoint — matches Students.js.
const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

// ── Brand tokens (match visionry.io) ──
const C = {
  bg:       '#061618',
  card:     '#0d3338',
  card2:    '#10434a',
  border:   '#10434a',
  borderHi: '#1d8a9a',
  teal:     '#25a2b3',
  tealMid:  '#1d8a9a',
  text:     '#e0f0f2',
  muted:    '#7fb8c0',
  green:    '#00cc88',
  greenBg:  '#06231a',
  red:      '#ff6b6b',
  redBorder:'#a13b3b',
  amber:    '#ffaa44'
};

function irishYmd(date) {
  return date.toLocaleDateString('en-CA', { timeZone: TZ });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-IE', {
    timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false
  });
}
function fmtDayShort(iso) {
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: TZ, weekday: 'short', day: '2-digit', month: 'short'
  });
}
// "in 40 min" / "in 2 hr 10 min" / "now" — the glanceable countdown for the hero.
function untilLabel(iso, nowIso) {
  const mins = Math.round((new Date(iso) - new Date(nowIso)) / 60000);
  if (mins <= 0)  return 'now';
  if (mins < 60)  return `in ${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m === 0 ? `in ${h} hr` : `in ${h} hr ${m} min`;
}

function StatCard({ label, value, accent, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        padding: 16, flex: 1, minWidth: 110,
        cursor: onClick ? 'pointer' : 'default'
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = C.teal; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor = C.border; }}
    >
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent || C.text }}>{value}</div>
      {onClick && <div style={{ fontSize: 11, color: C.teal, marginTop: 4 }}>Tap to review →</div>}
    </div>
  );
}

// Shared action button — same 44px touch-target pattern as Students.js.
// `fill` makes it expand to share a row equally (used on mobile).
// `filled` gives a solid `bg`/`color` pill instead of the outline style — used
// for resolved states (✓ Completed / ✗ No-show) so they read as "settled"
// rather than as a tappable action, while staying size-consistent.
function Btn({ children, onClick, color = C.muted, border = C.border, bg, disabled, fill, filled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: fill ? 1 : 'none',
        background: filled ? bg : 'none',
        border: filled ? 'none' : `1px solid ${border}`,
        borderRadius: 10,
        color, fontSize: 14, fontWeight: 600, padding: '11px 16px',
        minHeight: 44, whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1
      }}
    >
      {children}
    </button>
  );
}

// One lesson row with its mark buttons / resolved state. Stacks info above
// actions on mobile so the full-width buttons stay tappable.
function LessonRow({ lesson, busy, onComplete, onNoShow, showDate, isMobile }) {
  const state = lesson.completion_state;
  return (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center', gap: 12,
      padding: '12px 0', borderBottom: `1px solid ${C.border}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
        <span style={{ color: C.teal, fontWeight: 700, fontSize: 15, minWidth: 48 }}>
          {fmtTime(lesson.start_iso)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            {lesson.studentName}
            {state === 'completed' && lesson.edtNumber && (
              <span style={{ marginLeft: 8, fontSize: 11, color: C.teal, fontWeight: 700 }}>
                EDT {lesson.edtNumber}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            {showDate ? fmtDayShort(lesson.start_iso) + ' · ' : ''}
            {lesson.car_used === 'student' ? '🚗 own car' : "your car"}
            {lesson.location && ` · 📍 ${lesson.location}`}
          </div>
        </div>
      </div>

      {state === 'completed' ? (
        <Btn onClick={() => onNoShow(lesson)} disabled={busy === lesson.booking_id}
          filled color={C.green} bg={C.greenBg} fill={isMobile}>✓ Completed</Btn>
      ) : state === 'no_show' ? (
        <Btn onClick={() => onComplete(lesson)} disabled={busy === lesson.booking_id}
          filled color={C.red} bg="#2a0f0f" fill={isMobile}>✗ No-show</Btn>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={() => onComplete(lesson)} disabled={busy === lesson.booking_id}
            color={C.green} border={C.green} fill={isMobile}>Complete</Btn>
          <Btn onClick={() => onNoShow(lesson)} disabled={busy === lesson.booking_id}
            color={C.red} border={C.redBorder} fill={isMobile}>No-show</Btn>
        </div>
      )}
    </div>
  );
}

// ─── main ─────────────────────────────────────────────────────────────────

export default function Today({ instructor, onNavigate }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    const now         = new Date();
    const todayYmd    = irishYmd(now);
    const tomorrowYmd = irishYmd(new Date(now.getTime() + 86400000));
    const key         = instructor.instructor_key;
    const nowIso      = now.toISOString();
    const todayEndIso = `${todayYmd}T23:59:59Z`;

    const { data: todays } = await supabase
      .from('bookings')
      .select('booking_id, wa_id, start_iso, car_used, completion_state, location')
      .eq('instructor_key', key)
      .eq('status', 'booked')
      .gte('start_iso', `${todayYmd}T00:00:00Z`)
      .lte('start_iso', todayEndIso)
      .order('start_iso', { ascending: true });

    const { data: pendingPast } = await supabase
      .from('bookings')
      .select('booking_id, wa_id, start_iso, car_used, completion_state, location')
      .eq('instructor_key', key)
      .eq('status', 'booked')
      .eq('completion_state', 'pending')
      .lt('start_iso', `${todayYmd}T00:00:00Z`)
      .order('start_iso', { ascending: true });

    const { data: depositsDue } = await supabase
      .from('bookings')
      .select('booking_id, wa_id, start_iso, deposit_amount, payment_state')
      .eq('instructor_key', key)
      .eq('status', 'booked')
      .eq('payment_state', 'deposit_pending')
      .lte('start_iso', `${tomorrowYmd}T23:59:59Z`)
      .order('start_iso', { ascending: true });

    const { count: tomorrowCount } = await supabase
      .from('bookings').select('booking_id', { count: 'exact', head: true })
      .eq('instructor_key', key).eq('status', 'booked')
      .gte('start_iso', `${tomorrowYmd}T00:00:00Z`).lte('start_iso', `${tomorrowYmd}T23:59:59Z`);
    const { count: activeCount } = await supabase
      .from('students').select('wa_id', { count: 'exact', head: true })
      .eq('instructor_key', key).eq('approval_status', 'approved')
      .not('status', 'in', '("blocked","archived")');
    const { count: pendingApprovals } = await supabase
      .from('students').select('wa_id', { count: 'exact', head: true })
      .eq('instructor_key', key).eq('approval_status', 'pending');

    const allLessons = [...(pendingPast || []), ...(todays || [])];
    const waIds = [...new Set([...allLessons, ...(depositsDue || [])].map(b => b.wa_id))];

    const nameMap = {};
    const completedByStudent = {};
    if (waIds.length) {
      const { data: studs } = await supabase
        .from('students').select('wa_id, name').in('wa_id', waIds);
      for (const s of (studs || [])) nameMap[s.wa_id] = s.name;

      const { data: completed } = await supabase
        .from('bookings')
        .select('wa_id, start_iso')
        .in('wa_id', waIds)
        .eq('status', 'booked')
        .eq('completion_state', 'completed')
        .order('start_iso', { ascending: true });
      for (const b of (completed || [])) {
        (completedByStudent[b.wa_id] = completedByStudent[b.wa_id] || []).push(b.start_iso);
      }
    }

    function edtNumberFor(lesson) {
      const list = completedByStudent[lesson.wa_id] || [];
      const before = list.filter(iso => iso < lesson.start_iso).length;
      return before + 1;
    }

    const decorate = b => ({
      ...b,
      studentName: nameMap[b.wa_id] || b.wa_id,
      edtNumber: b.completion_state === 'completed' ? edtNumberFor(b) : null
    });

    setData({
      todayCount:       (todays || []).length,
      tomorrowCount:    tomorrowCount || 0,
      activeCount:      activeCount || 0,
      pendingApprovals: pendingApprovals || 0,
      needsMarking: (pendingPast || []).map(decorate),
      today:        (todays || []).map(decorate),
      depositsDue:  (depositsDue || []).map(b => ({ ...b, studentName: nameMap[b.wa_id] || b.wa_id })),
      nowIso
    });
  }, [instructor.instructor_key]);

  useEffect(() => { load(); }, [load]);

  async function mark(lesson, completion_state) {
    setBusy(lesson.booking_id);
    await supabase
      .from('bookings')
      .update({ completion_state, updated_at: new Date().toISOString() })
      .eq('booking_id', lesson.booking_id);

    if (completion_state === 'no_show') {
      const { data: s } = await supabase
        .from('students').select('no_show_count').eq('wa_id', lesson.wa_id).maybeSingle();
      await supabase
        .from('students')
        .update({ no_show_count: (s?.no_show_count || 0) + 1, updated_at: new Date().toISOString() })
        .eq('wa_id', lesson.wa_id);
    }
    await load();
    setBusy(null);
  }

  async function markDepositPaid(booking) {
    setBusy(booking.booking_id);
    await supabase
      .from('bookings')
      .update({ payment_state: 'deposit_paid', updated_at: new Date().toISOString() })
      .eq('booking_id', booking.booking_id);
    await load();
    setBusy(null);
  }

  if (!data) return <div style={{ color: C.muted, padding: 20, fontFamily: 'Poppins, sans-serif' }}>Loading…</div>;

  const firstName = (instructor.instructor_name || '').split(' ')[0] || 'there';
  const todayLabel = new Date().toLocaleDateString('en-GB', {
    timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long'
  });

  const todayPast     = data.today.filter(l => l.start_iso <= data.nowIso);
  const todayUpcoming = data.today.filter(l => l.start_iso > data.nowIso);
  const nextLesson    = todayUpcoming[0] || null; // the hero subject

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 500 }}>
        {todayLabel}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 18, color: C.text }}>Hey {firstName} 👋</div>

      {/* ── Signature: the next lesson (the one glance an instructor needs) ── */}
      {nextLesson ? (
        <div style={{
          background: `linear-gradient(140deg, ${C.card} 0%, ${C.card2} 100%)`,
          border: `1px solid ${C.borderHi}`, borderRadius: 18,
          padding: '20px 22px', marginBottom: 20, position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ fontSize: 11, color: C.teal, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600, marginBottom: 10 }}>
            Next lesson
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 34, fontWeight: 800, color: C.text, lineHeight: 1 }}>
              {fmtTime(nextLesson.start_iso)}
            </div>
            <div style={{ fontSize: 15, color: C.teal, fontWeight: 600 }}>
              {untilLabel(nextLesson.start_iso, data.nowIso)}
            </div>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginTop: 8 }}>
            {nextLesson.studentName}
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
            {nextLesson.car_used === 'student' ? '🚗 their own car' : 'your car'}
            {todayUpcoming.length > 1 && ` · ${todayUpcoming.length - 1} more after this`}
          </div>
        </div>
      ) : (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 18,
          padding: '22px', marginBottom: 20, textAlign: 'center'
        }}>
          <div style={{ fontSize: 15, color: C.muted }}>
            {data.todayCount > 0 ? "That's everyone for today — nice work. ✅" : 'No lessons today — enjoy the breather ☕'}
          </div>
        </div>
      )}

      {/* Stat cards — secondary context below the hero */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatCard label="Today" value={data.todayCount} accent={C.teal} />
        <StatCard label="Tomorrow" value={data.tomorrowCount} />
        <StatCard label="Active students" value={data.activeCount} />
        {data.pendingApprovals > 0 && (
          <StatCard label="To approve" value={data.pendingApprovals}
            accent={C.amber} onClick={() => onNavigate && onNavigate('students')} />
        )}
      </div>

      {/* ⚠ Needs marking */}
      {data.needsMarking.length > 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.amber}`,
          borderRadius: 14, padding: 18, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: C.amber, marginBottom: 4, fontWeight: 600 }}>
            ⚠ Needs marking ({data.needsMarking.length})
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
            Mark these so your EDT records stay accurate.
          </div>
          {data.needsMarking.map(l => (
            <LessonRow key={l.booking_id} lesson={l} busy={busy} isMobile={isMobile}
              onComplete={x => mark(x, 'completed')} onNoShow={x => mark(x, 'no_show')} showDate />
          ))}
        </div>
      )}

      {/* 💶 Deposits to collect */}
      {data.depositsDue.length > 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.amber}`,
          borderRadius: 14, padding: 18, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: C.amber, marginBottom: 8, fontWeight: 600 }}>
            💶 Deposits to collect ({data.depositsDue.length})
          </div>
          {data.depositsDue.map(b => (
            <div key={b.booking_id} style={{
              display: 'flex', flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center', gap: 12,
              padding: '10px 0', borderBottom: `1px solid ${C.border}`
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{b.studentName}</div>
                <div style={{ fontSize: 12, color: C.muted }}>
                  {fmtDayShort(b.start_iso)} · €{b.deposit_amount || '—'} pending
                </div>
              </div>
              <Btn onClick={() => markDepositPaid(b)} disabled={busy === b.booking_id}
                color={C.green} border={C.green} fill={isMobile}>
                Mark paid
              </Btn>
            </div>
          ))}
        </div>
      )}

      {/* Today's lessons */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18
      }}>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 12, fontWeight: 500 }}>Today's lessons</div>

        {data.today.length === 0 ? (
          <div style={{ color: C.muted, fontSize: 14, padding: '12px 0', textAlign: 'center' }}>
            No lessons today — enjoy the breather ☕
          </div>
        ) : (
          <>
            {todayPast.map(l => (
              <LessonRow key={l.booking_id} lesson={l} busy={busy}
                onComplete={x => mark(x, 'completed')} onNoShow={x => mark(x, 'no_show')} />
            ))}
            {todayUpcoming.map(l => (
              <div key={l.booking_id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0', borderBottom: `1px solid ${C.border}`, opacity: 0.65
              }}>
                <span style={{ color: C.teal, fontWeight: 700, fontSize: 15, minWidth: 48 }}>
                  {fmtTime(l.start_iso)}
                </span>
                <span style={{ flex: 1, fontSize: 14, color: C.text }}>{l.studentName}</span>
                <span style={{ fontSize: 11, color: C.muted }}>upcoming</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}